import {
  Button as MuiButton,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  type Row,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  columnsOf,
  entry,
  rowsOf,
} from '@/james-playground/aa-family-claim/claim/registry';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import {
  COLUMN,
  GATE,
  ROW_NOUN,
  TABLE,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  AddButton,
  CardShell,
  gateQuestion,
  EntryField,
  EntryToggle,
  hasProblem,
  Parts,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  cleanRows,
  columnIds,
  decidedBy,
  rows as rowsIn,
  storedAs,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

const isTable = (id: string) => entry(id).answerType === 'table';

/**
 * A table the form repeats: conditions and medicines, aids, professionals,
 * the people who help. Each row is a few labelled fields, added up to the
 * number the form has room for, and removed with the bin. A Yes/No that opens
 * a table (has she seen anyone else, does anyone help) sits above it, and the
 * table appears on a Yes.
 */
const RepeatCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible } = useDraft(ask, facts);
  const qid = useQids();
  const ids = ask.entries.filter(visible);
  const gates = ids.filter((id) => !isTable(id));
  const tables = ids.filter(isTable);

  // A table with nothing in it yet shows one empty row to start on.
  const rowsOf_ = (t: string): Row[] => {
    const r = rowsIn(draft[t]);
    return r.length ? r : [{}];
  };
  const setCell = (t: string, i: number, column: string, value: string) => {
    const next = rowsOf_(t).map((row, j) =>
      j === i ? { ...row, [column]: value } : row,
    );
    set(t, next);
  };
  const removeRow = (t: string, i: number) =>
    set(
      t,
      rowsOf_(t).filter((_, j) => j !== i),
    );
  const addRow = (t: string) => set(t, [...rowsOf_(t), {}]);

  const cleaned = (t: string) =>
    cleanRows(
      rowsOf_(t).map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([c, value]) => [
            c,
            storedAs(entry(c).answerType, value),
          ]),
        ),
      ),
      columnIds(t),
    );

  const ready =
    gates.every((g) => str(draft[g])) &&
    tables.every((t) => cleaned(t).length > 0) &&
    !tables.some((t) =>
      rowsOf_(t).some((row) =>
        Object.entries(row).some(([c, value]) => hasProblem(c, value)),
      ),
    );

  const noun = (t: string) =>
    ROW_NOUN[t] ?? { one: 'Entry', add: 'Add another' };

  const submit = () => {
    const values: Record<string, Value> = {};
    for (const g of gates) values[g] = str(draft[g]);
    for (const t of tables) values[t] = cleaned(t);
    // The reply names what was added; a gate shows only when it closed its table.
    const lines = ids.flatMap((id) => {
      if (!isTable(id)) {
        const opensShownTable = tables.some((t) => decidedBy(t).includes(id));
        return opensShownTable ? [] : [`${labelOf(id, v)}: ${values[id]}`];
      }
      const added = values[id] as Row[];
      const first = columnIds(id)[0];
      const names = added.map((row) => row[first]).filter(Boolean);
      return [
        names.length
          ? names.join(', ')
          : `${added.length} ${noun(id).one.toLowerCase()}`,
      ];
    });
    onSubmit(values, lines.join(' · '));
  };

  const none = TABLE.none[tables[0] ?? ''];
  const singleTableNoGate = tables.length === 1 && gates.length === 0;
  // One table needs no heading of its own: its question is just above it.
  const soleTable = tables.length === 1;

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Parts>
        {ask.entries
          .filter((id) => ids.includes(id))
          .map((id) => {
            if (!isTable(id))
              return (
                <EntryToggle
                  key={id}
                  id={id}
                  v={v}
                  {...(id === ask.gate
                    ? gateQuestion(ask, v)
                    : { question: GATE[id] ? say(GATE[id], v) : undefined })}
                  value={str(draft[id])}
                  onChange={(value) => set(id, value)}
                />
              );
            const t = id;
            const tableRows = rowsOf_(t);
            const limit = rowsOf(t);
            const columns = columnsOf(t);
            return (
              <Stack
                key={t}
                spacing={2}
                role='group'
                // A table that is the whole ask is named by the question above it.
                aria-label={soleTable ? say(ask.title, v) : undefined}
                aria-labelledby={soleTable ? undefined : qid(t)}
              >
                {!soleTable && (
                  <Typography id={qid(t)} variant='overline'>
                    {labelOf(t, v)}
                  </Typography>
                )}
                <Stack spacing={2} divider={<Divider flexItem />}>
                  {tableRows.map((row, i) => {
                    const name = TABLE.row(
                      noun(t).one,
                      i + 1,
                      tableRows.length,
                    );
                    return (
                      <Stack
                        key={i}
                        spacing={1.5}
                        role='group'
                        aria-labelledby={qid(`${t}-${i}`)}
                      >
                        <Stack
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                          direction='row'
                        >
                          <Typography id={qid(`${t}-${i}`)} variant='h6'>
                            {name}
                          </Typography>
                          {tableRows.length > 1 && (
                            <IconButton
                              aria-label={TABLE.remove(noun(t).one, i + 1)}
                              onClick={() => removeRow(t, i)}
                            >
                              <Icon icon='trash' fontSize={16} />
                            </IconButton>
                          )}
                        </Stack>
                        {columns.map((c) =>
                          c.answerType === 'yes_no' ? (
                            <FormControlLabel
                              key={c.id}
                              control={
                                <Checkbox
                                  checked={row[c.id] === 'Yes'}
                                  onChange={(e) =>
                                    setCell(
                                      t,
                                      i,
                                      c.id,
                                      e.target.checked ? 'Yes' : '',
                                    )
                                  }
                                />
                              }
                              label={TABLE.prescribed}
                            />
                          ) : (
                            <EntryField
                              key={c.id}
                              id={c.id}
                              v={v}
                              label={
                                COLUMN[c.id]
                                  ? say(COLUMN[c.id], v)
                                  : labelOf(c.id, v)
                              }
                              value={row[c.id] ?? ''}
                              onChange={(value) => setCell(t, i, c.id, value)}
                            />
                          ),
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
                {tableRows.length < limit ? (
                  <AddButton label={noun(t).add} onClick={() => addRow(t)} />
                ) : (
                  <Typography variant='body2'>{TABLE.full(limit)}</Typography>
                )}
                {none && singleTableNoGate && cleaned(t).length === 0 && (
                  <MuiButton
                    variant='outlined'
                    onClick={() => onSubmit({ [t]: [] }, say(none, v))}
                  >
                    {say(none, v)}
                  </MuiButton>
                )}
              </Stack>
            );
          })}
      </Parts>
    </CardShell>
  );
};

RepeatCard.displayName = 'RepeatCard';
export default RepeatCard;
